import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { useAppSocket, useIsSuccessful } from '@/common/hooks';
import { AppPaginationOptions, BaseQueryParams } from '@/common/interfaces';

import { DnsChangesInDomainOnConfigAttachResult, DomainConfigurationStatus, DomainDeploymentInfo, DomainGatewayKeys, DomainsQueryKeys, useGetAllDomainsQuery, useGetByIdDomainsQuery, useGetConfigDomainsQuery } from '@/modules/Domains';

interface Data {
  domainId: string;
  configInfo: DomainDeploymentInfo;
  dnsChangingResult?: DnsChangesInDomainOnConfigAttachResult;
}

interface Args {
  domainId?: string | null;
  pageParam?: AppPaginationOptions | null;
}

export const useUpdateDomainConfigurationStatus = ({ domainId, pageParam }: Args) => {
  const appMessageApi = AppMessage.useControls();
  const queryClient = useQueryClient();

  const isDomainDeploymentInfoSuccess = useIsSuccessful({ queryKey: [DomainsQueryKeys.GetConfig, domainId] });
  const isDomainByIdRequestSuccess = useIsSuccessful({ queryKey: [DomainsQueryKeys.GetOneById, domainId] });
  const isAllDomainsRequestSuccess = useIsSuccessful({ queryKey: [DomainsQueryKeys.GetAll, pageParam] });

  useAppSocket<Data>(DomainGatewayKeys.UpdateConfigurationStatus, ({ lastMessage }) => {
    const { domainId: id, configInfo, dnsChangingResult } = lastMessage;

    if (domainId) {
      if (isDomainDeploymentInfoSuccess && domainId === id) {
        queryClient.setQueryData<
          BaseQueryParams<typeof useGetConfigDomainsQuery>['data']
        >([DomainsQueryKeys.GetConfig, id], (data) => {
          return {
            ...data,
            ...configInfo,
          } as BaseQueryParams<typeof useGetConfigDomainsQuery>['data'];
        });
      }

      if (isDomainByIdRequestSuccess && domainId === id) {
        queryClient.setQueryData<
          BaseQueryParams<typeof useGetByIdDomainsQuery>['data']
        >([DomainsQueryKeys.GetOneById, id], (data) => {
          return {
            ...data,
            configurationStatus: configInfo?.configurationStatus,
            isConfigured: configInfo?.isConfigured === null ? data?.isConfigured : configInfo.isConfigured,
          } as BaseQueryParams<typeof useGetByIdDomainsQuery>['data'];
        });
      }

      if (configInfo?.configurationStatus === DomainConfigurationStatus.Failed && domainId === id) {
        appMessageApi.error('Failed to configure domain');
      }

      if (dnsChangingResult && dnsChangingResult.changeStatus !== 'notDefined' && domainId === id) {
        appMessageApi[dnsChangingResult.changeStatus](dnsChangingResult.message);
      }
    }

    if (isAllDomainsRequestSuccess) {
      queryClient.setQueryData<
        BaseQueryParams<typeof useGetAllDomainsQuery>['data']
      >([DomainsQueryKeys.GetAll, pageParam], (data) => {
        return {
          ...data,
          items: data?.items?.some(el => el.id === id)
            ? data.items.map((item) => {
              if (item.id === id) {
                return {
                  ...item,
                  isHttps: configInfo?.configInfo?.isHttps,
                  isDeployed: configInfo?.configInfo?.isHttps,
                  configurationStatus: configInfo?.configurationStatus,
                  isConfigured: configInfo?.isConfigured === null ? item.isConfigured : configInfo.isConfigured,
                };
              }
              return item;
            })
            : data?.items,
        } as BaseQueryParams<typeof useGetAllDomainsQuery>['data'];
      });
    }
  });
};
